import type { FC } from 'react'
import { Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import * as Sentry from '@sentry/nextjs'
import cn from 'classnames'
import { Link, Carousel } from 'components/ui'
import { useWindowSize } from 'hooks/use-window-size'
import { useState, useEffect } from 'react'
import type { ProductCarouselProps } from '.'
import s from './product-carousel.module.css'

class CarouselErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, {
      tags: { flow: 'carousel', component: 'ProductCarousel' },
      extra: { componentStack: info.componentStack },
    })
  }

  render() {
    if (this.state.hasError) return null
    return this.props.children
  }
}

const responsiveConfig = {
  wide: {
    items: 6,
    partialVisibilityGutter: 0,
  },
  desktop: {
    items: 4,
    partialVisibilityGutter: 0,
  },
  tablet: {
    items: 3,
    partialVisibilityGutter: 0,
  },
  mobile: {
    items: 2.2,
    partialVisibilityGutter: 0,
  },
}

const ProductCarousel: FC<ProductCarouselProps> = ({
  title,
  subtitle,
  className,
  rightContent,
  ssr = false,
  responsive = responsiveConfig,
  children,
}) => {
  // @ts-ignore
  const [resp, setResp] = useState<typeof responsiveConfig>(responsive)
  const { width } = useWindowSize()

  useEffect(() => {
    setResp({
      ...responsive,
      // @ts-ignore
      mobile: {
        ...responsive.mobile,
        items: width && width <= 320 ? 1.8 : responsive.mobile.items,
      },
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width])

  return (
    <div className={cn(s.root, className)}>
      {title && (
        <div className={s.header}>
          <div className={s.left}>
            <div className={s.title}>{title}</div>
            {subtitle && <p className={s.subtitle}>{subtitle}</p>}
          </div>
          {rightContent && (
            <div className={s.rightContent}>
              <Link href={rightContent.href}>{rightContent.title}</Link>
            </div>
          )}
        </div>
      )}
      <CarouselErrorBoundary>
        <Carousel config={resp} showArrows ssr={ssr} partialVisible className={s.carousel}>
          {children}
        </Carousel>
      </CarouselErrorBoundary>
    </div>
  )
}

export default ProductCarousel
